import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DataSetsPanel from 'components/DataSetsPanel/DataSetsPanel';
import DatePicker from 'components/DatePicker/DatePicker';
import NumberField from 'components/NumberField/NumberField';
import Select from 'components/Select/Select';
import TextField from 'components/TextField/TextField';
import { DATA_SETS_ENUM_TYPES } from 'constants/dataSets';
import {
  BUSINESS_UNIT_TYPE,
  CODE,
  DATE_CLOSED,
  DATE_OPENED,
  EQUITY_SHAREHOLDING,
  FINANCIAL_CONTROL,
  NAME,
  OWNERSHIP,
  UNIQUE_IDENTIFIER,
  UPSTREAM_TIER,
  VALUE_CHAIN_LIST,
  VALUE_CHAIN_SECTION,
} from 'constants/formFields';
import { DATE_FORMAT } from 'constants/general';
import { NUMBER_FORMATS } from 'constants/interfaces';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type IFieldError from 'store/types/IFieldError';
import type { IGeneralPanel } from '../interfaces/IGeneralPanel';

import styles from '../sass/CreateSupplierView.module.scss';

function GeneralPanel({
  control,
  enumData,
  dateOpened,
  onDateOpenedChange,
  recordId = '',
  placeholder,
  vcOptions,
  handleGetVcOptions,
  vcOptionsList,
  listPosition,
  setValue,
}: IGeneralPanel) {
  const { t } = useTranslation();

  return (
    <DataSetsPanel title={t('suppliers.selections.general')} className={styles.fullWidth}>
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={NAME}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <TextField
              name={name}
              value={value}
              label={t('suppliers.fields.supplierName')}
              error={error?.message}
              onChange={(event) => onChange(event.target.value)}
              required
              placeholder={placeholder || t('suppliers.fields.supplierName')}
            />
          )}
        />
        <Controller
          control={control}
          name={UNIQUE_IDENTIFIER}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <TextField
              name={name}
              value={value}
              label={t('suppliers.fields.uniqueIdentifier')}
              error={error?.message}
              onChange={(event) => onChange(event.target.value)}
              required
              placeholder={placeholder || t('suppliers.fields.uniqueIdentifier')}
              disabled={!!recordId}
            />
          )}
        />
      </div>
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={CODE}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <TextField
              name={name}
              value={value}
              label={t('suppliers.fields.supplierCode')}
              error={error?.message}
              onChange={(event) => onChange(event.target.value)}
              required
              placeholder={placeholder || t('suppliers.fields.supplierCode')}
            />
          )}
        />
      </div>
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={VALUE_CHAIN_LIST}
          render={({
            field: { name, value: valueChain, onChange: onChangeValueChain },
            fieldState: { error: errorCurrency },
          }) => (
            <Select
              name={name}
              placeholder={t('common.valueChainPosition')}
              label={t('common.valueChainPosition')}
              options={vcOptionsList}
              value={valueChain?.value ? valueChain : undefined}
              onChange={(value) => {
                const selectedValue = value as IOption;
                onChangeValueChain(selectedValue);
                handleGetVcOptions(selectedValue.value);
                setValue(VALUE_CHAIN_SECTION, undefined as never);
              }}
              error={errorCurrency?.message}
              menuPosition="absolute"
              isDisabled
              required
            />
          )}
        />
        <Controller
          control={control}
          name={VALUE_CHAIN_SECTION}
          render={({
            field: { name, value: valueChainSection, onChange: onChangeValueChainSection },
            fieldState: { error: errorCurrencySection },
          }) => (
            <Select
              name={name}
              placeholder={t('common.valueChainCategory')}
              label={t('common.valueChainCategory')}
              options={vcOptions || listPosition}
              value={valueChainSection?.value ? valueChainSection : null}
              onChange={onChangeValueChainSection}
              error={errorCurrencySection?.message}
              menuPosition="absolute"
              isDisabled={listPosition?.length === 0 && !recordId}
              required
            />
          )}
        />
        <Controller
          control={control}
          name={UPSTREAM_TIER}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <Select
              name={name}
              label={t('suppliers.fields.upstreamTier')}
              placeholder={placeholder || t('suppliers.fields.upstreamTier')}
              options={enumData?.[DATA_SETS_ENUM_TYPES.upstreamTier]}
              value={value?.value ? value : undefined}
              onChange={onChange}
              error={(error as IFieldError)?.value?.message}
              menuPosition="absolute"
              required
            />
          )}
        />
        <Controller
          control={control}
          name={OWNERSHIP}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <Select
              name={name}
              label={t('suppliers.fields.ownership')}
              placeholder={recordId ? placeholder : t('suppliers.fields.ownership')}
              options={enumData?.[DATA_SETS_ENUM_TYPES.ownership]}
              value={value?.value ? value : undefined}
              onChange={onChange}
              error={(error as IFieldError)?.message}
              menuPosition="absolute"
              required
            />
          )}
        />
        <Controller
          control={control}
          name={BUSINESS_UNIT_TYPE}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <Select
              name={name}
              label={t('suppliers.fields.physicalOrLogical')}
              placeholder={t('suppliers.fields.physicalOrLogical')}
              options={enumData?.[DATA_SETS_ENUM_TYPES.businessUnitType]}
              value={value?.value ? value : undefined}
              onChange={onChange}
              error={(error as IFieldError)?.value?.message}
              menuPosition="absolute"
              required
            />
          )}
        />
      </div>
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={EQUITY_SHAREHOLDING}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <NumberField
              name={name}
              value={value}
              label={t('suppliers.fields.equityShareholding')}
              error={error?.message}
              onChange={(values) => onChange(values.value)}
              type={NUMBER_FORMATS.percent}
              placeholder={placeholder || t('suppliers.fields.equityShareholding')}
              allowNegative={false}
              max={100}
            />
          )}
        />
        <Controller
          control={control}
          name={FINANCIAL_CONTROL}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <NumberField
              name={name}
              value={value}
              label={t('suppliers.fields.financialControl')}
              error={error?.message}
              onChange={(values) => onChange(values.value)}
              type={NUMBER_FORMATS.percent}
              placeholder={placeholder || t('suppliers.fields.financialControl')}
              allowNegative={false}
              max={100}
            />
          )}
        />
      </div>
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={DATE_OPENED}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <DatePicker
              label={t('suppliers.fields.dateOpened')}
              error={error?.message}
              onChange={(date) => {
                onDateOpenedChange(date);
                onChange(date);
              }}
              name={name}
              value={value}
              placeholder={DATE_FORMAT}
              maxDate={new Date()}
              required
            />
          )}
        />
        <Controller
          control={control}
          name={DATE_CLOSED}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <DatePicker
              label={t('suppliers.fields.dateClosed')}
              error={error?.message}
              onChange={onChange}
              name={name}
              value={value}
              minDate={dateOpened}
              placeholder={DATE_FORMAT}
            />
          )}
        />
      </div>
    </DataSetsPanel>
  );
}

export default GeneralPanel;
